/**
 * Session 19d — OCPP 2.0.1 Display Messages (v2.0.1-only). Mirrors the
 * Dexie `displayMessages` table into a reactive list for the Display
 * Messages tab. The CSMS-side mutations (SetDisplayMessage /
 * ClearDisplayMessage) happen in `csmsCallHandler`, which calls
 * `refresh` after each write; the tab's "Acknowledge" button removes a
 * message locally (UX validation — a real CP would clear it off the LCD).
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import { DisplayMessageRepositoryDexie } from '@/persistence/dexie/repositories';
import type { DisplayMessageSnapshot } from '@/domain';
import type { StationId } from '@/domain/types';

export interface DisplayMessageStoreState {
  readonly messages: readonly DisplayMessageSnapshot[];
  readonly status: 'idle' | 'ready';
}

export interface DisplayMessageStoreActions {
  refresh(stationId: StationId): Promise<void>;
  /** Local acknowledge — removes the message (same effect as ClearDisplayMessage). */
  acknowledge(stationId: StationId, id: number): Promise<void>;
}

export type DisplayMessageStore = DisplayMessageStoreState & DisplayMessageStoreActions;

const repo = new DisplayMessageRepositoryDexie(db);

export const useDisplayMessageStore = create<DisplayMessageStore>((set) => ({
  messages: [],
  status: 'idle',

  async refresh(stationId: StationId): Promise<void> {
    const messages = await repo.byStation(stationId);
    set({ messages: [...messages].sort((a, b) => a.id - b.id), status: 'ready' });
  },

  async acknowledge(stationId: StationId, id: number): Promise<void> {
    await repo.delete(stationId, id);
    const messages = await repo.byStation(stationId);
    set({ messages: [...messages].sort((a, b) => a.id - b.id) });
  },
}));
